import {
  type MessageRouter,
  type ReceiverInput,
  type ReceiverOutput,
} from "@pdfking/functionpool"
import {
  Channel,
  First,
  FullMethodDispatcher,
  ProcessIterable,
} from "@pdfking/tfunc"

import type { APIRequest } from "../../types/api/APIRequest"
import type { APIResponse } from "../../types/api/APIResponse"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { TaskPage } from "../../types/tasks/input/TaskPage"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskOptions } from "../../types/tasks/TaskOptions"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

import { TaskName } from "../../types/tasks/TaskName"
import { CreateEngineVirtual } from "../CreateEngineVirtual"
import { TaskDownload } from "../tasks/TaskDownload"
import { TaskMerge } from "../tasks/TaskMerge"
import { TaskPlot } from "../tasks/TaskPlot"
import { TaskRasterize } from "../tasks/TaskRasterize"
import { TaskSplit } from "../tasks/TaskSplit"
import { TaskZip } from "../tasks/TaskZip"

const ForwardToImplementation = FullMethodDispatcher<
  [TaskOptions, TrackingCallback, PdfEngine, ReceiverInput<APIRequest>],
  ReceiverOutput<APIResponse>
>({
  [TaskName.TASK_DOWNLOAD]: (task, emitter, engine) =>
    TaskDownload(engine, task, emitter),
  [TaskName.TASK_MERGE]: (task, emitter, engine, stream) =>
    TaskMerge(engine, task, emitter, stream as AsyncGenerator<TaskPage>),
  [TaskName.TASK_PLOT]: (task, emitter, engine) =>
    TaskPlot(engine, task, emitter),
  [TaskName.TASK_RASTERIZE]: (task, emitter, engine) =>
    TaskRasterize(engine, task, emitter),
  [TaskName.TASK_SPLIT]: (task, emitter, engine) =>
    TaskSplit(engine, task, emitter),
  [TaskName.TASK_ZIP]: (task, emitter, engine, stream) =>
    TaskZip(engine, task, emitter, stream as AsyncGenerator<TaskResult>),
})

export const APIReceiver = async function* (
  input: ReceiverInput<APIRequest>,
  forward: MessageRouter<APIRequest, APIResponse> // for the way we cache we should never use forward
): ReceiverOutput<APIResponse> {
  const task = (await First(input)) as TaskOptions
  const { push, stop, stream } = Channel<APIResponse>()

  const emit: TrackingCallback = async rest =>
    push({
      enum: TaskName.TRACKING,
      ...rest,
    })

  const engine = CreateEngineVirtual(task.options, forward)

  void ProcessIterable(
    ForwardToImplementation(task, emit, engine, input),
    push,
    stop
  )

  yield* stream
}
